import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, EmptyState } from '../../components';
import { getStartupById, StartupDoc, StartupDocument } from '../../services/startup-service';

const StartupPublic = () => {
  const { id } = useParams<{ id: string }>();
  const [startup, setStartup] = useState<StartupDoc | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!id) { setIsLoading(false); return; }
    getStartupById(id)
      .then(s => setStartup(s))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [id]);

  if (isLoading) {
    return <div className="section-gap"><p className="text-muted">Loading...</p></div>;
  }

  if (!startup) {
    return (
      <div className="section-gap">
        <EmptyState
          title="Startup Not Found"
          description="This startup profile does not exist or is not publicly available."
        />
      </div>
    );
  }

  const pitchDocs = startup.documents?.filter((d: StartupDocument) =>
    d.title?.toLowerCase().includes('pitch')
  ) || [];

  // Mock team data - in production comes from co-founders on the startup doc
  const team = (startup as any).coFounders || [];

  return (
    <div className="section-gap">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text">{startup.name}</h1>
          {startup.tagline && <p className="text-muted text-lg mt-1">{startup.tagline}</p>}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs bg-chart/10 text-chart px-3 py-1 rounded-full font-medium capitalize">
            {startup.industry}
          </span>
          <span className="text-xs bg-surface text-muted px-3 py-1 rounded-full capitalize">
            {startup.stage}
          </span>
        </div>
      </div>

      {/* Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* About */}
          <Card>
            <h2 className="text-xl font-semibold text-text mb-4">About</h2>
            {startup.description ? (
              <p className="text-muted text-sm leading-relaxed">{startup.description}</p>
            ) : (
              <p className="text-muted text-sm italic">No description provided.</p>
            )}
          </Card>

          {/* Problem & Solution */}
          {(startup.problemStatement || startup.solution) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {startup.problemStatement && (
                <Card>
                  <h3 className="text-lg font-semibold text-text mb-3">Problem</h3>
                  <p className="text-muted text-sm leading-relaxed">{startup.problemStatement}</p>
                </Card>
              )}
              {startup.solution && (
                <Card>
                  <h3 className="text-lg font-semibold text-text mb-3">Solution</h3>
                  <p className="text-muted text-sm leading-relaxed">{startup.solution}</p>
                </Card>
              )}
            </div>
          )}

          {/* Market & Revenue */}
          {(startup.targetMarket || startup.revenueModel) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {startup.targetMarket && (
                <Card>
                  <h3 className="text-lg font-semibold text-text mb-3">Target Market</h3>
                  <p className="text-muted text-sm leading-relaxed">{startup.targetMarket}</p>
                </Card>
              )}
              {startup.revenueModel && (
                <Card>
                  <h3 className="text-lg font-semibold text-text mb-3">Revenue Model</h3>
                  <p className="text-muted text-sm leading-relaxed">{startup.revenueModel}</p>
                </Card>
              )}
            </div>
          )}

          {/* Traction */}
          {startup.traction && (
            <Card>
              <h3 className="text-lg font-semibold text-text mb-3">Traction</h3>
              <p className="text-muted text-sm leading-relaxed">{startup.traction}</p>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Key Info */}
          <Card>
            <h3 className="text-lg font-semibold text-text mb-4">Details</h3>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted">Stage</span>
                <span className="text-text font-medium capitalize">{startup.stage}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted">Industry</span>
                <span className="text-text font-medium">{startup.industry}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted">Team Size</span>
                <span className="text-text font-medium">{startup.teamSize}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted">Founded</span>
                <span className="text-text font-medium">{startup.foundingYear}</span>
              </div>
              {startup.website && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted">Website</span>
                  <a href={startup.website} target="_blank" rel="noopener noreferrer" className="text-chart hover:underline truncate ml-4">
                    {startup.website.replace(/^https?:\/\//, '')}
                  </a>
                </div>
              )}
            </div>
          </Card>

          {/* Team */}
          <Card>
            <h3 className="text-lg font-semibold text-text mb-4">Team</h3>
            {/* Founder (always shown) */}
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 bg-chart rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                F
              </div>
              <div>
                <p className="text-sm font-medium text-text">Founder</p>
                <p className="text-xs text-muted">CEO</p>
              </div>
            </div>
            {team.length > 0 ? (
              team.map((member: any, index: number) => (
                <div key={index} className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 bg-surface rounded-full flex items-center justify-center text-muted text-sm font-medium flex-shrink-0">
                    {member.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text">{member.name}</p>
                    <p className="text-xs text-muted">{member.role}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-muted text-xs">Solo founder</p>
            )}
          </Card>

          {/* Pitch Deck */}
          <Card>
            <h3 className="text-lg font-semibold text-text mb-4">Pitch Deck</h3>
            {pitchDocs.length > 0 ? (
              <div className="space-y-2">
                {pitchDocs.map((doc: StartupDocument) => (
                  <a
                    key={doc._id}
                    href={doc.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-surface rounded-lg border border-line hover:bg-black/5 transition-colors"
                  >
                    <svg className="w-6 h-6 text-chart flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                    </svg>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-text truncate">{doc.title || 'Pitch Deck'}</p>
                      <p className="text-xs text-muted">{new Date(doc.uploadedAt).toLocaleDateString()}</p>
                    </div>
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-muted text-sm">No pitch deck available yet.</p>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default StartupPublic;
